import React, { useState } from "react";
import { Box, Button, Card, Grid, Typography } from "@mui/material";

// Component to display a list of items with "View More" functionality
const ExpandableList = ({ items, renderItem, initialVisible = 20 }) => {
    const [showAll, setShowAll] = useState(false);
    const displayItems = showAll ? items : items.slice(0, initialVisible);
    const hasMore = items.length > initialVisible;

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" flexWrap="wrap" gap={1}>
                {displayItems.map((item, idx) => (
                    <React.Fragment key={idx}>
                        {renderItem(item, idx)}
                    </React.Fragment>
                ))}
            </Box>
            {hasMore && (
                <Button 
                    size="small" 
                    onClick={() => setShowAll(!showAll)}
                    sx={{ 
                        textTransform: 'none',
                        fontSize: '12px',
                        width: 'fit-content',
                        p: '2px 8px',
                        minWidth: 'auto',
                        color: '#1976d2',
                        '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline',
                        }
                    }}
                >
                    {showAll ? 'View Less' : `View ${items.length - initialVisible} more`}
                </Button>
            )}
        </Box>
    );
};

// Common item style
const itemStyle = {
    background: "#f3f3f3",
    padding: "3px 12px",
    fontSize: "12px",
    minWidth: "auto",
    maxWidth: "max-content",
    borderRadius: "40px",
    textAlign: "center"
};

export default function ViewCarouselFilters({ CarouselId, carouselName, storeId, filters, stores }) {
    // Get store name from storeId
    const storeName = storeId && stores?.stores?.length > 0
        ? stores.stores.find(store => store?.storeId === storeId)?.storeName || "-"
        : "-";
console.log("filters", filters);

    // Get counts and data
    const brandsCount = filters?.brands?.length || 0;
    const categoriesCount = filters?.categories?.length || 0;
    const strainsCount = filters?.strains?.length || 0;
    const traitsCount = filters?.traits?.length || 0;
    const pageName = filters?.pageId || [];
    const minProducts = filters?.productLimit?.min || "-";
    const maxProducts = filters?.productLimit?.max || "-";

    return (
        <Box display="flex" flexDirection="column">
            <Typography variant="h6" fontWeight="semibold" mb={1}>Selected Filters</Typography>
            <Card sx={{ borderRadius: 2.5, boxShadow: "none", p: 3 }}>
                {/* <Typography variant="h6" fontWeight="bold" mb={2}>Applied Filters</Typography> */}
                <ul style={{ listStyle: "none", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "18px" }}>
                    <li>
                        <Typography variant="body2" fontWeight="semibold" color="#2E2E3A">
                            Carousel Name
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography
                                variant="body2"
                                fontWeight="medium"
                                color="#707070"
                                style={{
                                    background: "#f3f3f3",
                                    padding: "3px 12px",
                                    fontSize: "12px",
                                    minWidth: "auto",
                                    maxWidth: "max-content",
                                    borderRadius: "40px",
                                    textAlign: "center"
                                }}
                            >
                                {carouselName}
                            </Typography>
                        </Box>
                    </li>
                    {/* Page Name */}
                    <li>
                        <Typography variant="body2" fontWeight="semibold" color="#2E2E3A" mb={0.5}>
                            Page Name
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        {Array.isArray(pageName) && pageName.length > 0 ? (
                                pageName.map((name, idx) => (
                                    <Typography
                                        key={idx}
                                        variant="body2"
                                        fontWeight="medium"
                                        color="#707070"
                                        style={{
                                            background: "#f3f3f3",
                                            padding: "3px 12px",
                                            fontSize: "12px",
                                            minWidth: "auto",
                                            maxWidth: "max-content",
                                            borderRadius: "40px",
                                            textAlign: "center",
                                            marginRight: "6px",
                                            marginBottom: "4px"
                                        }}
                                    >
                                        {name}
                                    </Typography>
                                ))
                            ) : (
                                <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    color="#707070"
                                    style={{
                                        background: "#f3f3f3",
                                        padding: "3px 12px",
                                        fontSize: "12px",
                                        minWidth: "auto",
                                        maxWidth: "max-content",
                                        borderRadius: "40px",
                                        textAlign: "center"
                                    }}
                                >
                                    -
                                </Typography>
                            )}
                        </Box>
                    </li>

                    {/* Product Quantity Limits */}
                    <li>
                        <Typography variant="body2" fontWeight="semibold" color="#2E2E3A" mb={0.5}>
                            Minimum Product Qty
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography variant="body2" fontWeight="medium" color="#707070" style={{ padding: "5px 0", fontSize: "12px" }}>
                                {minProducts}
                            </Typography>
                        </Box>
                    </li>
                    <li>
                        <Typography variant="body2" fontWeight="semibold" color="#2E2E3A" mb={0.5}>
                            Maximum Product Qty
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography variant="body2" fontWeight="medium" color="#707070" style={{ padding: "5px 0", fontSize: "12px" }}>
                                {maxProducts}
                            </Typography>
                        </Box>
                    </li>
                    <li>
                        <Typography variant="body2" fontWeight="semibold" color="#2E2E3A" mb={0.5}>
                            Brands ({brandsCount})
                        </Typography>
                        {brandsCount > 0 ? (
                            <ExpandableList 
                                items={filters.brands} 
                                renderItem={(brand, idx) => (
                                    <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                        color="#707070"
                                        style={itemStyle}
                                    >
                                        {brand?.brandName || "-"}
                                    </Typography>
                                )}
                            />
                        ) : (
                            <Typography variant="body2" fontWeight="medium" color="#707070" style={{ padding: "5px 0" }}>
                                -
                            </Typography>
                        )}
                    </li>

                    {/* Categories */}
                    <li>
                        <Typography variant="body2" fontWeight="semibold" color="#2E2E3A" mb={0.5}>
                            Categories ({categoriesCount})
                        </Typography>
                        {categoriesCount > 0 ? (
                            <ExpandableList 
                                items={filters.categories} 
                                renderItem={(category, idx) => (
                                    <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                        color="#707070"
                                        style={itemStyle}
                                    >
                                        {category?.categoryName || "-"}
                                    </Typography>
                                )}
                            />
                        ) : (
                            <Typography variant="body2" fontWeight="medium" color="#707070" style={{ padding: "5px 0" }}>
                                -
                            </Typography>
                        )}
                    </li>

                    {/* Strain Types */}
                    <li>
                        <Typography variant="body2" fontWeight="semibold" color="#2E2E3A" mb={0.5}>
                            Strain Types ({strainsCount})
                        </Typography>
                        {strainsCount > 0 ? (
                            <ExpandableList 
                                items={filters.strains} 
                                renderItem={(strain, idx) => (
                                    <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                        color="#707070"
                                        style={itemStyle}
                                    >
                                        {strain?.strainType || "-"}
                                    </Typography>
                                )}
                            />
                        ) : (
                            <Typography variant="body2" fontWeight="medium" color="#707070" style={{ padding: "5px 0" }}>
                                -
                            </Typography>
                        )}
                    </li>

                    {/* Product Traits */}
                    <li>
                        <Typography variant="body2" fontWeight="semibold" color="#2E2E3A" mb={0.5}>
                            Product Traits ({traitsCount})
                        </Typography>
                        {traitsCount > 0 ? (
                            <ExpandableList 
                                items={filters.traits} 
                                renderItem={(trait, idx) => (
                                    <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                        color="#707070"
                                        style={itemStyle}
                                    >
                                        {trait?.subTraitName || "-"}
                                    </Typography>
                                )}
                            />
                        ) : (
                            <Typography variant="body2" fontWeight="medium" color="#707070" style={{ padding: "5px 0" }}>
                                -
                            </Typography>
                        )}
                    </li>

                    {/* Store (only show if storeId exists) */}
                    {storeId ? (
                        <li>
                            <Typography variant="body2" fontWeight="semibold" color="#2E2E3A" mb={0.5}>
                                Store
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    color="#707070"
                                    style={{
                                        background: "#f3f3f3",
                                        padding: "3px 12px",
                                        fontSize: "12px",
                                        minWidth: "auto",
                                        maxWidth: "max-content",
                                        borderRadius: "40px",
                                        textAlign: "center"
                                    }}
                                >
                                    {storeName}
                                </Typography>
                            </Box>
                        </li>
                    ) : null}
                </ul>
            </Card>
        </Box>
    );
}
